import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Download, ArrowLeft, Shield, FileSignature } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AdminAgreements = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, NDA, RCA
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (authLoading) return;

    if (!user || user.role !== 'admin') {
      alert('Access denied. Admin privileges required.');
      navigate('/admin');
      return;
    }

    fetchAgreements();
  }, [user, authLoading, navigate]);

  const fetchAgreements = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/agreements`, {
        headers: {
          'Authorization': `Bearer ${user.email}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAgreements(data.agreements || []);
      }
    } catch (error) {
      console.error('Error fetching agreements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (agreementId, filename) => {
    try {
      // Use direct link download to bypass fetch interceptors
      const downloadUrl = `${BACKEND_URL}/api/agreements/download/${agreementId}`;
      
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = downloadUrl;
      a.download = filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download PDF');
    }
  };

  const filteredAgreements = agreements.filter(ag => {
    if (filter === 'all') return true;
    return ag.agreement_type === filter;
  });

  const ndaCount = agreements.filter(ag => ag.agreement_type === 'NDA').length;
  const rcaCount = agreements.filter(ag => ag.agreement_type === 'RCA').length;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Signed Agreements</h1>
                <p className="mt-1 text-sm text-gray-600">View and download all NDA and RCA agreements</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Agreements</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{agreements.length}</div>
              <p className="text-xs text-muted-foreground">All signed agreements</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">NDAs Signed</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{ndaCount}</div>
              <p className="text-xs text-muted-foreground">Non-Disclosure Agreements</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">RCAs Signed</CardTitle>
              <FileSignature className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{rcaCount}</div>
              <p className="text-xs text-muted-foreground">Research Collaboration Agreements</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-3 mb-6">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
          >
            All ({agreements.length})
          </Button>
          <Button
            variant={filter === 'NDA' ? 'default' : 'outline'}
            onClick={() => setFilter('NDA')}
          >
            NDAs ({ndaCount})
          </Button>
          <Button
            variant={filter === 'RCA' ? 'default' : 'outline'}
            onClick={() => setFilter('RCA')}
          >
            RCAs ({rcaCount})
          </Button>
        </div>

        {/* Agreements Table */}
        <Card>
          <CardHeader>
            <CardTitle>Signed Agreements</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredAgreements.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No agreements found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Type</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Signatory</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Email</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Role</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Language</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Signed Date</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Hash</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAgreements.map((agreement) => (
                      <tr key={agreement.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            agreement.agreement_type === 'NDA' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {agreement.agreement_type}
                          </span>
                        </td>
                        <td className="py-3 px-4">{agreement.signatory_name}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{agreement.signatory_email}</td>
                        <td className="py-3 px-4 capitalize">{agreement.signatory_role}</td>
                        <td className="py-3 px-4 uppercase">{agreement.language}</td>
                        <td className="py-3 px-4 text-sm">
                          {new Date(agreement.signed_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-xs font-mono text-gray-500">
                          {agreement.signature_hash?.substring(0, 12)}...
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(agreement.id, agreement.pdf_filename)}
                            className="flex items-center gap-2"
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminAgreements;
